/**
 * DungeonExplorer (O-025) — client-local walk mode for a generated interior.
 *
 * While active it owns the camera (WASD walk + a third-person orbit driven by
 * pointer drag and the wheel) and the game swallows its normal move-input sampling
 * (the layer's `blocksGameInput()`), so NOTHING is sent on the wire and the
 * server-authoritative player never moves. Esc (or the HUD button) exits back to
 * the shrine.
 *
 * The interior is assembled far outside every zone's bounds (x/z +1600) so it can
 * never visually collide with the live world; fog + the offset hide it until entry.
 *
 * Two of the owner's three reports are cured here, under
 * `q-dungeon-playable-not-preview` half (A):
 *
 * - **Walls are SOLID.** The walker resolves against `BuiltInterior.collision`
 *   with the shared sim's own `resolveCircle`, not a soft clamp to the interior
 *   AABB (H-550 ⇒ AD-341).
 * - **The view is THIRD PERSON.** The camera no longer sits at the walker's eye.
 *   A visible walker proxy stands at the walk position and the camera rides the
 *   game's own boom — `orbitPose()`, the very function `OrbitCamera.update` calls
 *   — pulled in against the interior's collision by `explorerCamera.ts` so a boom
 *   inside a 16.5 m chamber does not end up inside the rock.
 *
 * Still open and deliberately NOT done here: the square-grid LAYOUT, which is a
 * design change to the generator (half (B)), and server authority / protocol /
 * combat on this path, each of which is its own decision.
 */

import * as THREE from 'three';

import type { DungeonCatalog, DungeonPlan } from '@great-cloud/procedural-dungeons';
import {
  COLLISION_RELAXATION_PASSES,
  PLAYER_COLLISION_RADIUS_M,
  resolveCircle,
} from '@great-cloud/shared/client';

import { disposeObject3D } from '../assets/gpuOwnership.js';
import { orbitPose } from '../camera.js';
import { enterActiveSlot } from '../scene/activeSlot.js';
import { runTeardown } from '../teardown.js';
import { boomDistance, clampDungeonDist, clampDungeonPitch } from './explorerCamera.js';
import { buildDungeonInterior, type BuiltInterior, type LoaderFor } from './interior.js';
import { previewSpotPose, type PreviewSpot } from './previewSpot.js';

const OFFSET = new THREE.Vector3(1600, 0, 1600);
const WALK_SPEED = 5.2; // m/s
/** Capture presentation walks at half pace so a 12 s clip glides rather than rushes. */
const PRESENT_WALK_FACTOR = 0.5;
const LOOK_SPEED = 0.0042;
/** Wheel notch, matching the open-world orbit camera's feel. */
const ZOOM_STEP = 0.9;

/**
 * The visible walker. Deliberately the SAME proxy shape the game builds for a
 * player entity before a rig avatar loads (`Game.buildEntityMesh`): a capsule
 * plus a nose cone for facing. Third person needs something to be third person
 * OF; matching the existing proxy keeps this from becoming a second, divergent
 * idea of what a player looks like. The rigged avatar is not wired here —
 * server authority on this path is absent and is its own decision.
 */
function buildWalkerProxy(): THREE.Group {
  const group = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.4, 0.9, 4, 12),
    new THREE.MeshStandardMaterial({ color: '#9fd0e8', roughness: 0.6 }),
  );
  body.position.y = 0.85;
  body.castShadow = true;
  group.add(body);
  const nose = new THREE.Mesh(
    new THREE.ConeGeometry(0.16, 0.34, 8),
    new THREE.MeshStandardMaterial({ color: '#d6ecf7' }),
  );
  nose.rotation.x = Math.PI / 2;
  nose.position.set(0, 1.05, 0.48);
  nose.castShadow = true;
  group.add(nose);
  return group;
}

/**
 * Compile `root`'s shader programs for rendering into `scene` through `camera`
 * without stalling the main thread — the Game passes the renderer's
 * `compileAsync` (KHR_parallel_shader_compile where the context has it).
 */
export type InteriorPrecompile = (
  root: THREE.Object3D,
  camera: THREE.Camera,
  scene: THREE.Scene,
) => Promise<unknown>;

export class DungeonExplorer {
  active = false;
  private built: BuiltInterior | null = null;
  private plan: DungeonPlan | null = null;
  private keys = new Set<string>();
  private yaw = 0;
  private pitch = 0.42;
  private dist = 5.5;
  private position = new THREE.Vector3();
  private facing = 0;
  private walker: THREE.Group | null = null;
  private savedCamera: { position: THREE.Vector3; quaternion: THREE.Quaternion } | null = null;
  private dragging = false;
  private lastPointer = { x: 0, y: 0 };
  private onExit: (() => void) | null = null;
  private banner: HTMLDivElement | null = null;
  /** Capture presentation (dev hook only): no banner, no walker proxy, a slow automatic walk. */
  private presenting = false;
  /** Restores the render camera's layers (E3.3.S4 active slot); null while outside. */
  private releaseSlot: (() => void) | null = null;

  private readonly keyDown = (event: KeyboardEvent): void => {
    if (!this.active) return;
    if (event.code === 'Escape') {
      this.exit();
      event.stopPropagation();
      return;
    }
    this.keys.add(event.code);
    if (['KeyW', 'KeyA', 'KeyS', 'KeyD', 'Space'].includes(event.code)) {
      event.stopPropagation();
      event.preventDefault();
    }
  };
  private readonly keyUp = (event: KeyboardEvent): void => {
    this.keys.delete(event.code);
  };
  private readonly pointerDown = (event: PointerEvent): void => {
    if (!this.active) return;
    this.dragging = true;
    this.lastPointer = { x: event.clientX, y: event.clientY };
  };
  private readonly pointerUp = (): void => {
    this.dragging = false;
  };
  /**
   * Orbit the boom. Signs match `OrbitCamera`'s drag handler exactly (yaw falls
   * as the pointer moves right; dragging DOWN raises the pitch), so the dungeon
   * does not invert the controls the rest of the game teaches.
   */
  private readonly pointerMove = (event: PointerEvent): void => {
    if (!this.active || !this.dragging) return;
    this.yaw -= (event.clientX - this.lastPointer.x) * LOOK_SPEED;
    this.pitch = clampDungeonPitch(this.pitch + (event.clientY - this.lastPointer.y) * LOOK_SPEED);
    this.lastPointer = { x: event.clientX, y: event.clientY };
  };
  /** Zoom, clamped to the dungeon sub-range of CAMERA_BOUNDS (never wider). */
  private readonly wheel = (event: WheelEvent): void => {
    if (!this.active) return;
    this.dist = clampDungeonDist(this.dist + Math.sign(event.deltaY) * ZOOM_STEP);
    event.preventDefault();
  };

  constructor(
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly loaderFor: LoaderFor,
    private readonly catalog: DungeonCatalog,
    private readonly precompile: InteriorPrecompile | null = null,
  ) {}

  async enter(plan: DungeonPlan, onExit: () => void): Promise<void> {
    if (this.active) return;
    const theme = this.catalog.themes.find((entry) => entry.id === plan.themeId);
    if (!theme) return;
    this.built = await buildDungeonInterior(
      plan,
      theme,
      this.catalog,
      this.loaderFor,
      OFFSET.clone(),
    );
    // S4 finding (design §12d): the first interior frame used to compile every
    // program synchronously — > 60 s frozen on the dev host's ANGLE-GL path.
    // Compile first, against the light set the frame will really have: the slot
    // narrows the camera (interior + the scene's top-level lights) while the
    // interior is NOT yet in the scene, so its lights are counted exactly once;
    // released straight after the synchronous half so the open world keeps
    // drawing while the programs finish. Failure only costs the old stall.
    if (this.precompile) {
      const built = this.built;
      const release = enterActiveSlot(this.scene, this.camera, built.group);
      let ready: Promise<unknown>;
      try {
        ready = this.precompile(built.group, this.camera, this.scene);
      } catch {
        ready = Promise.resolve();
      } finally {
        release();
      }
      await ready.catch(() => undefined);
      if (this.built !== built) return;
    }
    this.scene.add(this.built.group);
    this.plan = plan;
    this.savedCamera = {
      position: this.camera.position.clone(),
      quaternion: this.camera.quaternion.clone(),
    };
    this.position.copy(this.built.entrance).setY(0);
    this.yaw = 0;
    this.pitch = 0.42;
    this.dist = 5.5;
    this.facing = 0;
    this.walker = buildWalkerProxy();
    this.built.group.add(this.walker);
    // E3.3.S4 (AD-158 §6): only the interior graph is submitted while inside —
    // the exterior zone contributes zero draw calls. Taken after the walker joins
    // the group, because the slot is granted once, to a complete subtree.
    this.releaseSlot = enterActiveSlot(this.scene, this.camera, this.built.group);
    this.active = true;
    this.onExit = onExit;
    window.addEventListener('keydown', this.keyDown, true);
    window.addEventListener('keyup', this.keyUp, true);
    window.addEventListener('pointerdown', this.pointerDown, true);
    window.addEventListener('pointerup', this.pointerUp, true);
    window.addEventListener('pointermove', this.pointerMove, true);
    window.addEventListener('wheel', this.wheel, { capture: true, passive: false });
    this.showBanner(plan);
  }

  frame(dtMs: number): void {
    if (!this.active || !this.built) return;
    const dt = dtMs / 1000;
    const forward = new THREE.Vector3(Math.sin(this.yaw), 0, Math.cos(this.yaw));
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const move = new THREE.Vector3();
    if (this.keys.has('KeyW') || this.presenting) move.add(forward);
    if (this.keys.has('KeyS')) move.sub(forward);
    if (this.keys.has('KeyA')) move.sub(right);
    if (this.keys.has('KeyD')) move.add(right);
    if (move.lengthSq() > 0) {
      move.normalize();
      // The walker turns to face where it is going; idle keeps the last facing
      // rather than snapping back to the camera, so a stopped walker does not
      // spin when the boom is orbited around it.
      this.facing = Math.atan2(move.x, move.z);
      move.multiplyScalar(WALK_SPEED * (this.presenting ? PRESENT_WALK_FACTOR : 1) * dt);
      this.position.add(move);
      // Walls are solid: the walker is a circle resolved against the interior's
      // derived static collision with EXACTLY the shared sim's resolver, so it
      // agrees with the code the server runs (AD-026/AD-044). Collision is in the
      // interior's local frame, so the group offset comes off and goes back on.
      const resolved = resolveCircle(
        this.position.x - OFFSET.x,
        this.position.z - OFFSET.z,
        PLAYER_COLLISION_RADIUS_M,
        this.built.collision,
        COLLISION_RELAXATION_PASSES,
      );
      this.position.x = resolved.x + OFFSET.x;
      this.position.z = resolved.z + OFFSET.z;
    }
    // The walker proxy is a CHILD of the interior group, whose own position is
    // the +1600 offset — so it takes LOCAL coordinates.
    if (this.walker) {
      this.walker.position.set(this.position.x - OFFSET.x, 0, this.position.z - OFFSET.z);
      this.walker.rotation.y = this.facing;
    }
    // Third person: the boom is the GAME'S boom (`orbitPose`), shortened only as
    // far as the interior's own collision demands. The pull-in is computed in the
    // interior's local frame, like every other collision query on this path.
    // `orbitPose` takes the target's FEET and aims `ORBIT_LOOK_HEIGHT` above them
    // — the same contract `Game` uses when it passes `ownMesh.position` — so the
    // dungeon framing matches the open world instead of inventing a second one.
    const dist = boomDistance(
      this.position.x - OFFSET.x,
      this.position.z - OFFSET.z,
      this.yaw,
      this.pitch,
      this.dist,
      this.built.collision,
    );
    const pose = orbitPose(this.position, this.yaw, this.pitch, dist);
    this.camera.position.set(pose.x, pose.y, pose.z);
    this.camera.lookAt(pose.lookX, pose.lookY, pose.lookZ);
  }

  exit(): void {
    if (!this.active) return;
    this.active = false;
    window.removeEventListener('keydown', this.keyDown, true);
    window.removeEventListener('keyup', this.keyUp, true);
    window.removeEventListener('pointerdown', this.pointerDown, true);
    window.removeEventListener('pointerup', this.pointerUp, true);
    window.removeEventListener('pointermove', this.pointerMove, true);
    window.removeEventListener('wheel', this.wheel, true);
    // E3.3.S4: every step runs even if one throws (the shared teardown
    // discipline), so a disposal failure can never leave the camera narrowed to
    // the slot layer — which would render the exterior as nothing at all.
    runTeardown([
      () => {
        const release = this.releaseSlot;
        this.releaseSlot = null;
        release?.();
      },
      () => {
        // The walker is a child of the interior group, so the traverse below disposes
        // its geometry/materials with everything else; only the reference is dropped.
        this.walker = null;
        this.plan = null;
        const built = this.built;
        this.built = null;
        if (!built) return;
        this.scene.remove(built.group);
        disposeObject3D(built.group); // H-1349: owned only — templates stay the cache's
      },
      () => {
        if (this.savedCamera) {
          this.camera.position.copy(this.savedCamera.position);
          this.camera.quaternion.copy(this.savedCamera.quaternion);
          this.savedCamera = null;
        }
        this.keys.clear();
        this.presenting = false;
        this.banner?.remove();
        this.banner = null;
      },
      () => {
        const onExit = this.onExit;
        this.onExit = null;
        onExit?.();
      },
    ]);
  }

  /**
   * Dev hook only (S4 visual lane, `__GC_DEVDUNGEON`): stand the walker at a
   * named spot of the entered plan, facing into it. False when not inside or
   * when the plan has no such spot — never a stand-in position. The pose is
   * resolved against the interior's collision like any step, so a spot can
   * never park the walker inside a wall.
   */
  placeAt(spot: PreviewSpot): boolean {
    if (!this.active || !this.built || !this.plan) return false;
    const pose = previewSpotPose(this.plan, spot);
    if (!pose) return false;
    const resolved = resolveCircle(
      pose.x,
      pose.z,
      PLAYER_COLLISION_RADIUS_M,
      this.built.collision,
      COLLISION_RELAXATION_PASSES,
    );
    this.position.set(resolved.x + OFFSET.x, 0, resolved.z + OFFSET.z);
    this.yaw = pose.yaw;
    this.facing = pose.yaw;
    return true;
  }

  /**
   * Dev hook only (capture footage): hide the debug banner and the capsule walker proxy and
   * walk forward slowly. The capsule is a placeholder that must never reach public content, so
   * presentation removes it rather than relying on the frame to dodge it. False when not inside.
   */
  setPresentation(on: boolean): boolean {
    if (!this.active) return false;
    this.presenting = on;
    if (this.walker) this.walker.visible = !on;
    if (this.banner) this.banner.style.display = on ? 'none' : '';
    return true;
  }

  /** The interior group while inside, else null — the active slot (E3.3.S4 scene dump). */
  slotRoot(): THREE.Object3D | null {
    return this.built?.group ?? null;
  }

  private showBanner(plan: DungeonPlan): void {
    const banner = document.createElement('div');
    banner.style.cssText =
      'position:fixed;top:10px;left:50%;transform:translateX(-50%);z-index:60;' +
      'background:rgba(11,16,32,0.88);color:#dfe7ec;padding:8px 14px;border:1px solid #c8a23a;' +
      'border-radius:8px;font:13px system-ui;pointer-events:none;text-align:center';
    banner.textContent =
      `Dungeon preview — ${plan.difficultyId} · ${plan.themeId} · ${plan.rooms.length} rooms · ` +
      // AD-404 §11: the attempts-to-valid signal, on the client's own diagnostic surface.
      `seed ${plan.effectiveSeed} · attempt ${plan.generationAttempt} — WASD walk, drag to orbit, wheel to zoom, Esc to leave ` +
      `(techdemo, client-local)`;
    document.body.appendChild(banner);
    this.banner = banner;
  }
}
